import React, { useState, useCallback, useRef, useMemo } from 'react';
import {
  View,
  Text,
  ScrollView,
  Pressable,
  TextInput,
  ActivityIndicator,
  Keyboard,
  Image,
} from 'react-native';
import { useRouter } from 'expo-router';
import {
  Search,
  MapPin,
  Globe,
  Building2,
  Calendar,
  Users,
  DollarSign,
  ChevronRight,
  X,
  SlidersHorizontal,
} from 'lucide-react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useAuth } from '@/lib/supabase';
import { useUserRole } from '@/lib/auth/role';
import type { Href } from 'expo-router';
import { ScreenFrame } from '@/components/ScreenFrame';
import { useResponsive } from '@/lib/useResponsive';
import { AppLayout, Breadcrumb } from '@/components/AppLayout';
import { KeyboardAwareBody } from '@/components/modal/KeyboardAwareBody';
import {
  useCompanyDirectory,
  useDirectoryAutocomplete,
  useCompanyDirectoryCount,
  type CompanyDirectoryEntry,
  type CompanyDirectoryFilters,
  directoryStatusLabel,
  isLifecycleDirectoryStatus,
} from '@/lib/supabase/company-directory';
import { ClosedCompaniesList } from '@/components/company/ClosedCompaniesList';
import type { ClosedDirectoryEntry } from '@/lib/company/public-card';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Location options are populated dynamically from the directory data.
// These are common regions to start with — will grow as the directory expands.
const LOCATIONS = [
  'Zürich', 'Geneva', 'Basel', 'Lausanne', 'Bern', 'Zug',
  'London', 'Berlin', 'Paris', 'Amsterdam', 'Munich',
  'San Francisco', 'New York', 'Singapore',
];

const INDUSTRIES = [
  'FinTech', 'HealthTech', 'AI/ML', 'SaaS', 'CleanTech', 'BioTech',
  'EdTech', 'PropTech', 'FoodTech', 'Cybersecurity', 'IoT', 'Blockchain',
  'MedTech', 'InsurTech', 'LegalTech', 'HRTech', 'AgriTech',
];

const STAGES = [
  { value: 'pre-seed', label: 'Pre-Seed' },
  { value: 'seed', label: 'Seed' },
  { value: 'series-a', label: 'Series A' },
  { value: 'series-b', label: 'Series B' },
  { value: 'series-c+', label: 'Series C+' },
  { value: 'growth', label: 'Growth' },
];

const PAGE_SIZE = 30;


// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function CompanyDirectoryScreen() {
  const router = useRouter();
  const colors = useThemeColors();

  /**
   * ROLE-AWARE, AND RESOLVED FROM THE SERVER ROLE ON PURPOSE.
   *
   * Every in-app route here is an investor one (Dealflow's toolbar and both of
   * its empty states), so a hardcoded startup fallback sent an investor with no
   * history into `(startup)`, whose guard refuses them and bounces to `/` — a
   * visible unauthorized round-trip instead of a return.
   *
   * But founders DO arrive here: `directory-invite-claim` emails them a link to
   * this page so they can claim their company. That path is a cold deep link
   * with no history, which is exactly when the fallback fires — so it has to be
   * right for both personas, and it cannot read `useProfileStore`, whose
   * `userRole` is not persisted and is null on a cold start. `useUserRole` is
   * the server-fetched one; same pattern as `strategic-analysis.tsx`.
   */
  const { user: directoryUser } = useAuth();
  const { data: directoryRole } = useUserRole(directoryUser?.id);
  const directoryParent: Href =
    directoryRole === 'startup'
      ? '/(startup)/startup-dashboard'
      : directoryRole === 'admin'
        ? '/(admin)/admin-dashboard'
        : directoryRole === 'investor'
          ? '/(investor)/dealflow'
          // Role not resolved yet (a cold deep link is exactly that moment).
          // Guessing a persona here is how the original bug worked; '/' is the
          // app's own dispatcher and routes to whichever group the user
          // actually belongs to.
          : '/';
  const { isDesktop } = useResponsive();

  const breadcrumbs: Breadcrumb[] = useMemo(() => [
    { label: 'Dealflow', href: '/(investor)/dealflow' },
    { label: 'Startup Directory' },
  ], []);

  // Search & filters
  const [searchText, setSearchText] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [selectedIndustry, setSelectedIndustry] = useState<string | null>(null);
  const [selectedStage, setSelectedStage] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  // 119-07: the closed / acquired / bankrupt companies, a separate list.
  const [showClosed, setShowClosed] = useState(false);
  const [page, setPage] = useState(0);
  const searchInputRef = useRef<TextInput>(null);

  const filters: CompanyDirectoryFilters = useMemo(
    () => ({
      search: activeSearch || undefined,
      canton: selectedLocation ?? undefined,
      industry: selectedIndustry ?? undefined,
      stage: selectedStage ?? undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [activeSearch, selectedLocation, selectedIndustry, selectedStage, page]
  );

  // Queries — always hit real DB (company_directory has real Zefix data)
  const { data: companies = [], isLoading } = useCompanyDirectory(filters);
  const { data: autocompleteResults = [] } = useDirectoryAutocomplete(
    showAutocomplete ? searchText : ''
  );
  const { data: totalCount = 0 } = useCompanyDirectoryCount();

  const displayCompanies = companies;
  const displayCount = totalCount;

  // Handlers
  const handleSearch = useCallback(() => {
    setActiveSearch(searchText);
    setShowAutocomplete(false);
    setPage(0);
    Keyboard.dismiss();
  }, [searchText]);

  const handleAutocompleteSelect = useCallback(
    (name: string) => {
      setSearchText(name);
      setActiveSearch(name);
      setShowAutocomplete(false);
      setPage(0);
      Keyboard.dismiss();
    },
    []
  );

  const handleClearSearch = useCallback(() => {
    setSearchText('');
    setActiveSearch('');
    setShowAutocomplete(false);
    setPage(0);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSelectedLocation(null);
    setSelectedIndustry(null);
    setSelectedStage(null);
    setPage(0);
  }, []);

  const hasActiveFilters = selectedLocation || selectedIndustry || selectedStage;

  const handleCompanyPress = useCallback(
    (company: CompanyDirectoryEntry) => {
      router.push({
        pathname: '/startup-detail',
        params: {
          directoryId: company.id,
          source: 'directory',
        },
      });
    },
    [router]
  );

  // A closed company opens its public record (startup-detail's sealed path).
  const handleClosedPress = useCallback(
    (entry: ClosedDirectoryEntry) => {
      router.push({ pathname: '/startup-detail', params: { id: entry.startupId } });
    },
    [router]
  );

  // Formatting helpers
  const formatFunding = (amount: number | null) => {
    if (!amount) return null;
    if (amount >= 1_000_000_000) return `CHF ${(amount / 1_000_000_000).toFixed(1)}B`;
    if (amount >= 1_000_000) return `CHF ${(amount / 1_000_000).toFixed(1)}M`;
    if (amount >= 1_000) return `CHF ${(amount / 1_000).toFixed(0)}K`;
    return `CHF ${amount}`;
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <>
      {/* This route is registered with headerShown:false (_layout.tsx), so the
         title/headerStyle below were inert — a native header that never drew.
         ScreenFrame now supplies the title, the back control and the top inset
         that nothing owned. The registration is left alone; only the dead
         options are removed. */}
      <AppLayout title="Startup Directory" breadcrumbs={breadcrumbs}>
        <ScreenFrame
          title="Startup Directory"
          back={{ fallback: directoryParent }}
          bottomEdge
        >
          <KeyboardAwareBody
            className="flex-1"
            style={{ backgroundColor: colors.surface.secondary }}
            keyboardShouldPersistTaps="handled"
          >
            {/* Header stats */}
            <View className="px-4 pb-2">
              <Text
                className="text-sm"
                style={{ color: colors.text.secondary }}
                numberOfLines={1}
              >
                {displayCount.toLocaleString()} startups in the directory
              </Text>
            </View>

          {/* Search bar with autocomplete */}
          <View className="px-4 pt-2 pb-1 relative z-20">
            <View
              className="flex-row items-center rounded-xl px-3 py-2.5"
              style={{ backgroundColor: colors.surface.primary }}
            >
              <Search size={18} color={colors.text.secondary} />
              <TextInput
                ref={searchInputRef}
                className="flex-1 ml-2 text-base"
                style={{ color: colors.text.primary }}
                placeholder="Search by name, industry, city..."
                placeholderTextColor={colors.text.secondary}
                value={searchText}
                onChangeText={(text) => {
                  setSearchText(text);
                  setShowAutocomplete(text.length >= 2);
                }}
                onSubmitEditing={handleSearch}
                returnKeyType="search"
              />
              {searchText.length > 0 && (
                <Pressable onPress={handleClearSearch} hitSlop={8}>
                  <X size={18} color={colors.text.secondary} />
                </Pressable>
              )}
            </View>

            {/* Autocomplete dropdown */}
            {showAutocomplete && autocompleteResults.length > 0 && (
              <View
                className="absolute left-4 right-4 top-14 rounded-xl shadow-lg z-30 overflow-hidden"
                style={{
                  backgroundColor: colors.surface.primary,
                  borderWidth: 1,
                  borderColor: colors.border.light,
                }}
              >
                {autocompleteResults.map((item) => (
                  <Pressable
                    key={item.id}
                    className="flex-row items-center px-3 py-3 border-b"
                    style={{ borderColor: colors.border.light }}
                    onPress={() => handleAutocompleteSelect(item.name)}
                  >
                    {item.logo_url ? (
                      <Image
                        source={{ uri: item.logo_url }}
                        className="w-8 h-8 rounded-lg mr-3"
                        style={{ backgroundColor: colors.surface.secondary }}
                        resizeMode="contain"
                      />
                    ) : (
                      <View
                        className="w-8 h-8 rounded-lg items-center justify-center mr-3"
                        style={{ backgroundColor: colors.surface.secondary }}
                      >
                        <Building2 size={16} color={colors.text.secondary} />
                      </View>
                    )}
                    <View className="flex-1">
                      <Text
                        className="text-sm font-medium"
                        style={{ color: colors.text.primary }}
                      >
                        {item.name}
                      </Text>
                      <Text
                        className="text-xs"
                        style={{ color: colors.text.secondary }}
                      >
                        {[item.industry, item.canton].filter(Boolean).join(' · ')}
                      </Text>
                    </View>
                    <ChevronRight size={14} color={colors.text.secondary} />
                  </Pressable>
                ))}
              </View>
            )}
          </View>

          {/* Filter toggle + active filters */}
          <View className="px-4 py-2 flex-row items-center gap-2 flex-wrap">
            <Pressable
              className="flex-row items-center px-3 py-1.5 rounded-full"
              style={{
                backgroundColor: showFilters
                  ? colors.primary[600]
                  : colors.surface.secondary,
              }}
              onPress={() => setShowFilters(!showFilters)}
            >
              <SlidersHorizontal
                size={14}
                color={showFilters ? '#FFF' : colors.text.secondary}
              />
              <Text
                className="text-xs font-medium ml-1.5"
                style={{
                  color: showFilters ? '#FFF' : colors.text.secondary,
                }}
              >
                Filters
              </Text>
            </Pressable>

            <Pressable
              testID="directory-closed-toggle"
              accessibilityRole="button"
              accessibilityState={{ selected: showClosed }}
              accessibilityLabel={showClosed ? 'Show active companies' : 'Show closed companies'}
              className="flex-row items-center px-3 py-1.5 rounded-full"
              style={{ backgroundColor: showClosed ? colors.text.primary : colors.surface.secondary }}
              onPress={() => setShowClosed((v) => !v)}
            >
              <Text
                numberOfLines={1}
                className="text-xs font-medium"
                style={{ color: showClosed ? colors.surface.primary : colors.text.secondary }}
              >
                Closed
              </Text>
            </Pressable>

            {/* Active filter chips */}
            {selectedLocation && (
              <FilterChip
                label={selectedLocation}
                onRemove={() => setSelectedLocation(null)}
                colors={colors}
              />
            )}
            {selectedIndustry && (
              <FilterChip
                label={selectedIndustry}
                onRemove={() => setSelectedIndustry(null)}
                colors={colors}
              />
            )}
            {selectedStage && (
              <FilterChip
                label={STAGES.find((s) => s.value === selectedStage)?.label ?? selectedStage}
                onRemove={() => setSelectedStage(null)}
                colors={colors}
              />
            )}
            {hasActiveFilters && (
              <Pressable onPress={handleClearFilters}>
                <Text
                  className="text-xs font-medium"
                  style={{ color: colors.primary[600] }}
                >
                  Clear all
                </Text>
              </Pressable>
            )}
          </View>

          {/* Filter panel */}
          {showFilters && (
            <Animated.View entering={FadeInDown.duration(200)} className="px-4 pb-3">
              <View
                className="rounded-xl p-3"
                style={{ backgroundColor: colors.surface.primary }}
              >
                {/* Location filter */}
                <Text
                  className="text-xs font-semibold mb-2"
                  style={{ color: colors.text.secondary }}
                >
                  LOCATION
                </Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} className="mb-3" style={{ flexGrow: 0 }}>
                  <View className="flex-row gap-1.5">
                    {LOCATIONS.map((location) => (
                      <Pressable
                        key={location}
                        className="px-2.5 py-1 rounded-full"
                        style={{
                          backgroundColor:
                            selectedLocation === location
                              ? colors.primary[600]
                              : colors.surface.secondary,
                        }}
                        onPress={() =>
                          setSelectedLocation(
                            selectedLocation === location ? null : location
                          )
                        }
                      >
                        <Text
                          className="text-xs font-medium"
                          style={{
                            color:
                              selectedLocation === location
                                ? '#FFF'
                                : colors.text.secondary,
                          }}
                        >
                          {location}
                        </Text>
                      </Pressable>
                    ))}
                  </View>
                </ScrollView>

                {/* Industry filter */}
                <Text
                  className="text-xs font-semibold mb-2"
                  style={{ color: colors.text.secondary }}
                >
                  INDUSTRY
                </Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} className="mb-3" style={{ flexGrow: 0 }}>
                  <View className="flex-row gap-1.5">
                    {INDUSTRIES.map((industry) => (
                      <Pressable
                        key={industry}
                        className="px-2.5 py-1 rounded-full"
                        style={{
                          backgroundColor:
                            selectedIndustry === industry
                              ? colors.primary[600]
                              : colors.surface.secondary,
                        }}
                        onPress={() =>
                          setSelectedIndustry(
                            selectedIndustry === industry ? null : industry
                          )
                        }
                      >
                        <Text
                          className="text-xs font-medium"
                          style={{
                            color:
                              selectedIndustry === industry
                                ? '#FFF'
                                : colors.text.secondary,
                          }}
                        >
                          {industry}
                        </Text>
                      </Pressable>
                    ))}
                  </View>
                </ScrollView>

                {/* Stage filter */}
                <Text
                  className="text-xs font-semibold mb-2"
                  style={{ color: colors.text.secondary }}
                >
                  STAGE
                </Text>
                <View className="flex-row flex-wrap gap-1.5">
                  {STAGES.map((stage) => (
                    <Pressable
                      key={stage.value}
                      className="px-2.5 py-1 rounded-full"
                      style={{
                        backgroundColor:
                          selectedStage === stage.value
                            ? colors.primary[600]
                            : colors.surface.secondary,
                      }}
                      onPress={() =>
                        setSelectedStage(
                          selectedStage === stage.value ? null : stage.value
                        )
                      }
                    >
                      <Text
                        className="text-xs font-medium"
                        style={{
                          color:
                            selectedStage === stage.value
                              ? '#FFF'
                              : colors.text.secondary,
                        }}
                      >
                        {stage.label}
                      </Text>
                    </Pressable>
                  ))}
                </View>
              </View>
            </Animated.View>
          )}

          {/* Closed companies (119-07): search applies; the filters do not. */}
          {showClosed && (
            <View className="px-4 pb-8">
              <ClosedCompaniesList search={activeSearch} onOpen={handleClosedPress} />
            </View>
          )}

          {/* Loading state */}
          {!showClosed && isLoading && (
            <View className="items-center py-12">
              <ActivityIndicator size="large" color={colors.primary[600]} />
              <Text
                className="text-sm mt-3"
                style={{ color: colors.text.secondary }}
              >
                Loading directory...
              </Text>
            </View>
          )}

          {/* Company list */}
          {!showClosed && !isLoading && (
            <View className="px-4 pb-8">
              {displayCompanies.length === 0 ? (
                <View className="items-center py-12">
                  <Building2 size={40} color={colors.text.secondary} />
                  <Text
                    className="text-base font-medium mt-3"
                    style={{ color: colors.text.primary }}
                  >
                    No companies found
                  </Text>
                  <Text
                    className="text-sm mt-1 text-center"
                    style={{ color: colors.text.secondary }}
                  >
                    Try adjusting your search or filters
                  </Text>
                </View>
              ) : (
                <View className="gap-3">
                  {displayCompanies.map((company, index) => (
                    <Animated.View
                      key={company.id}
                      entering={FadeInDown.delay(index * 30).duration(300)}
                    >
                      <CompanyCard
                        company={company}
                        colors={colors}
                        onPress={() => handleCompanyPress(company)}
                        formatFunding={formatFunding}
                      />
                    </Animated.View>
                  ))}
                </View>
              )}

              {/* Load more */}
              {displayCompanies.length >= PAGE_SIZE && (
                <Pressable
                  className="items-center py-4 mt-4"
                  onPress={() => setPage((p) => p + 1)}
                >
                  <Text
                    className="text-sm font-medium"
                    style={{ color: colors.primary[600] }}
                  >
                    Load more
                  </Text>
                </Pressable>
              )}
            </View>
          )}
          </KeyboardAwareBody>
        </ScreenFrame>
      </AppLayout>
    </>
  );
}

// ---------------------------------------------------------------------------
// Company Card
// ---------------------------------------------------------------------------

function CompanyCard({
  company,
  colors,
  onPress,
  formatFunding,
}: {
  company: CompanyDirectoryEntry;
  colors: any;
  onPress: () => void;
  formatFunding: (n: number | null) => string | null;
}) {
  const isClaimed = company.status === 'claimed';
  // 119-07: a closed / acquired / bankrupt row (visible to admins and to the
  // investor who owns a private row) shows its lifecycle status, never
  // "unclaimed" — it cannot be claimed.
  const lifecycle = isLifecycleDirectoryStatus(company.status);
  const badge = lifecycle
    ? company.status === 'acquired'
      ? { fg: colors.primary[600], bg: colors.primary[50] }
      : company.status === 'bankrupt'
        ? { fg: colors.red.main, bg: colors.red.light }
        : { fg: colors.text.secondary, bg: colors.surface.tertiary }
    : isClaimed
      ? { fg: '#34C759', bg: '#E8F9ED' }
      : { fg: '#FF9500', bg: '#FFF4E6' };

  return (
    <Pressable
      className="rounded-xl overflow-hidden p-4"
      style={{ backgroundColor: colors.surface.primary }}
      onPress={onPress}
    >
      <View className="flex-row items-start">
        {/* Logo / placeholder */}
        {company.logo_url ? (
          <Image
            source={{ uri: company.logo_url }}
            className="w-12 h-12 rounded-xl mr-3"
            style={{ backgroundColor: colors.surface.secondary }}
            resizeMode="contain"
          />
        ) : (
          <View
            className="w-12 h-12 rounded-xl items-center justify-center mr-3"
            style={{ backgroundColor: colors.surface.secondary }}
          >
            <Text className="text-base font-bold" style={{ color: colors.text.secondary }}>
              {company.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
            </Text>
          </View>
        )}

        <View className="flex-1">
          {/* Name + badge row */}
          <View className="flex-row items-center gap-2">
            <Text
              className="text-base font-semibold flex-shrink"
              style={{ color: colors.text.primary }}
              numberOfLines={1}
            >
              {company.name}
            </Text>
            <View
              testID="directory-status-badge"
              className="px-2 py-0.5 rounded-full shrink-0"
              style={{ backgroundColor: badge.bg }}
            >
              <Text numberOfLines={1} className="text-[10px] font-bold" style={{ color: badge.fg }}>
                {directoryStatusLabel(company.status)}
              </Text>
            </View>
          </View>

          {/* Industry + location */}
          <View className="flex-row items-center mt-1 gap-3">
            {company.industry && (
              <View className="flex-row items-center">
                <Building2 size={12} color={colors.text.secondary} />
                <Text
                  className="text-xs ml-1"
                  style={{ color: colors.text.secondary }}
                >
                  {company.industry}
                </Text>
              </View>
            )}
            {(company.canton || company.city) && (
              <View className="flex-row items-center">
                <MapPin size={12} color={colors.text.secondary} />
                <Text
                  className="text-xs ml-1"
                  style={{ color: colors.text.secondary }}
                >
                  {[company.city, company.canton].filter(Boolean).join(', ')}
                </Text>
              </View>
            )}
          </View>

          {/* Description */}
          {company.description && (
            <Text
              className="text-xs mt-1.5 leading-4"
              style={{ color: colors.text.secondary }}
              numberOfLines={2}
            >
              {company.description}
            </Text>
          )}

          {/* Metadata row */}
          <View className="flex-row items-center mt-2 gap-4">
            {company.founded_year && (
              <View className="flex-row items-center">
                <Calendar size={11} color={colors.text.tertiary} />
                <Text
                  className="text-[11px] ml-1"
                  style={{ color: colors.text.tertiary }}
                >
                  {company.founded_year}
                </Text>
              </View>
            )}
            {company.team_size && (
              <View className="flex-row items-center">
                <Users size={11} color={colors.text.tertiary} />
                <Text
                  className="text-[11px] ml-1"
                  style={{ color: colors.text.tertiary }}
                >
                  {company.team_size}+
                </Text>
              </View>
            )}
            {company.total_funding && (
              <View className="flex-row items-center">
                <DollarSign size={11} color={colors.text.tertiary} />
                <Text
                  className="text-[11px] ml-1 font-medium"
                  style={{ color: colors.text.tertiary }}
                >
                  {formatFunding(company.total_funding)}
                </Text>
              </View>
            )}
            {company.website && (
              <View className="flex-row items-center">
                <Globe size={11} color={colors.text.tertiary} />
                <Text
                  className="text-[11px] ml-1"
                  style={{ color: colors.text.tertiary }}
                  numberOfLines={1}
                >
                  {company.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                </Text>
              </View>
            )}
          </View>
        </View>

        <ChevronRight size={16} color={colors.text.secondary} />
      </View>
    </Pressable>
  );
}

// ---------------------------------------------------------------------------
// Filter Chip
// ---------------------------------------------------------------------------

function FilterChip({
  label,
  onRemove,
  colors,
}: {
  label: string;
  onRemove: () => void;
  colors: any;
}) {
  return (
    <View
      className="flex-row items-center px-2.5 py-1 rounded-full"
      style={{ backgroundColor: `${colors.primary[600]}15` }}
    >
      <Text
        className="text-xs font-medium mr-1"
        style={{ color: colors.primary[600] }}
      >
        {label}
      </Text>
      <Pressable onPress={onRemove} hitSlop={4}>
        <X size={12} color={colors.primary[600]} />
      </Pressable>
    </View>
  );
}
